import { useRouteContext, useRouter } from "@tanstack/react-router";

import { Id } from "@cvx/_generated/dataModel";
import { User } from "~/types";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { formatCurrency } from "@/utils/format";
import { useAccount } from "@/state/useAccount";
import { useQuery } from "@tanstack/react-query";

export function Header({ user }: { user: User }) {
  const router = useRouter();
  const { appId, creatorId } = useAccount();
  const routeContext = useRouteContext({
    from: router.state.matches.slice(-1)[0].id,
  });
  const { contractId } = useAccount();

  // For the dashboard route, override the header with dynamic content
  const isDashboardRoute = router.state.matches.some(
    (match) => match.routeId === "/_app/_auth/dashboard/_layout/",
  );

  const isPartnershipRoute = router.state.matches.some(
    (match) => match.routeId === "/_app/_auth/dashboard/_layout/partnerships/",
  );

  const isCustomLeaderboardRoute = router.state.matches.some(
    (match) =>
      match.routeId === "/_app/_auth/dashboard/_layout/customLeaderboard",
  );

  // Fetch custom leaderboard data for header
  const { data: customLeaderboard } = useQuery({
    ...convexQuery(api.creators.getCreatorCustomLeaderboard, {
      creatorId: creatorId as Id<"creators">,
    }),
    enabled: !!creatorId && isCustomLeaderboardRoute,
  });

  const currentPartnership = user?.creatorPartnerships.find(
    (partnership) => partnership._id === contractId,
  );

  // Fetch dashboard data for budget remaining (only for creator dashboard)
  const { data: dashboardData } = useQuery({
    ...convexQuery(api.dashboard.getCreatorDashboard, { contractId }),
    enabled: !!contractId && !appId && isDashboardRoute,
  });

  const headerTitle = isDashboardRoute
    ? contractId
      ? currentPartnership?.campaign.name
      : appId
        ? "Campaigns"
        : "Your Progress"
    : isPartnershipRoute
      ? appId
        ? "Creator Partnerships"
        : "Discover Partnerships"
      : isCustomLeaderboardRoute
        ? customLeaderboard?.name || "Custom Leaderboard"
        : routeContext?.headerTitle;

  const headerDescription = isDashboardRoute
    ? appId
      ? ""
      : contractId
        ? (() => {
            const parts = [];

            // Add budget remaining if available
            if (
              dashboardData?.remainingBudget !== undefined &&
              dashboardData?.campaign?.prizePool
            ) {
              parts.push(
                `${formatCurrency(dashboardData.remainingBudget)} budget remaining`,
              );
            }

            // Add CPM as X,XXX per 1M views
            const cpmRate = currentPartnership?.cpmRate?.rate;
            if (cpmRate) {
              // CPM is per 1000 views, so multiply by 1000 to get per 1M views
              const perMillionViews = cpmRate * 1000;
              parts.push(`${formatCurrency(perMillionViews)} per 1M views`);
            }

            // If we have info to show, return it; otherwise show date range
            if (parts.length > 0) {
              return parts.join(" | ");
            }

            // Fallback to original date range display
            if (currentPartnership?.startDate && currentPartnership?.endDate) {
              return (
                currentPartnership?.app.name +
                " | " +
                new Date(currentPartnership.startDate).toLocaleDateString(
                  "en-US",
                  {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  },
                ) +
                " - " +
                new Date(currentPartnership.endDate).toLocaleDateString(
                  "en-US",
                  {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  },
                )
              );
            }

            return currentPartnership?.app.name || "";
          })()
        : "View and manage all your active campaigns"
    : isPartnershipRoute
      ? appId
        ? "Manage your new & existing creators."
        : "Find & manage your next app partnership."
      : isCustomLeaderboardRoute
        ? "See how you stack up against other creators."
        : routeContext?.headerDescription;

  return headerTitle && headerDescription ? (
    <header className="z-10 flex w-full flex-col border-b border-border bg-card px-6">
      <div className="mx-auto flex w-full max-w-screen-xl items-center justify-between py-12">
        <div className="flex flex-col items-start gap-2">
          <h1 className="text-3xl font-medium text-primary/80">
            {headerTitle}
          </h1>
          <p className="text-base font-normal text-primary/60">
            {headerDescription}
          </p>
        </div>
      </div>
    </header>
  ) : null;
}
